import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { format } from 'date-fns'
import { RankmiTable } from '@rankmi/rankmi-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileCertificate } from '@fortawesome/pro-solid-svg-icons/faFileCertificate'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons/faEllipsisV'
import { useTranslation } from 'react-i18next'
import { Button, Dropdown, Menu } from 'antd'
import { IMainState } from 'store/states'
import EmptyState from 'components/reusables/emptyState'
import { dc3AttestationsService } from 'api/administration/dc3Attestations'
import { useNotification } from 'hooks/notifications/useNotification'
import { sortTableEnum } from 'enum/sortTable.enum'
import {
  IDc3Attestation,
  IDc3AttestationDetail,
} from 'interfaces/dc3Attestation'
import { SortValue } from 'models/rankmiTable.model'
import AssociatedCoursesCell from './components/associatedCoursesCell'
import AssociatedCoursesModal from './components/associatedCoursesModal'
import DeleteAttestationModal from './components/deleteAttestationModal'
import Dc3AttestationForm from './components/dc3AttestationForm'
import AssociateCoursesView from './components/associateCoursesView'
import Dc4ReportGenerateModal from './components/Dc4ReportGenerateModal'
import { TableHeaderActions, RankmiTableWrapper, Options } from './styled'

type FormView = { mode: 'create' } | { mode: 'edit'; id: number } | null
type AssociateView = { attestationId: number } | null

interface IProps {
  onSecondaryViewChange?: (isSecondary: boolean) => void
}

const SORT_FIELD_MAP: Record<string, string> = {
  name: 'name',
  createdAt: 'created_at',
  associatedCoursesCount: 'associated_courses',
}

const LegalAttestations = ({ onSecondaryViewChange }: IProps) => {
  const { t } = useTranslation()
  const notification = useNotification()
  const dc4Enabled = useSelector(
    ({ general }: IMainState) => general.permissions.dc4Enabled
  )
  const prefix = 'administration.certificate.legalAttestationsList'
  const [attestations, setAttestations] = useState<IDc3Attestation[]>([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchText, setSearchText] = useState('')
  const [sort, setSort] = useState<SortValue | null>(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteProcessing, setDeleteProcessing] = useState(false)
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null)
  const [associatedCoursesAttestationId, setAssociatedCoursesAttestationId] =
    useState<number | null>(null)
  const [selectedAttestation, setSelectedAttestation] =
    useState<IDc3Attestation | null>(null)
  const [formView, setFormView] = useState<FormView>(null)
  const [associateView, setAssociateView] = useState<AssociateView>(null)
  const [dc4ModalOpen, setDc4ModalOpen] = useState(false)

  const fetchAttestations = useCallback(async () => {
    setLoading(true)
    try {
      const order =
        sort?.field && sort?.order
          ? `${SORT_FIELD_MAP[sort.field] || sort.field} ${
              sortTableEnum[sort.order as keyof typeof sortTableEnum]
            }`
          : undefined

      const response = await dc3AttestationsService.getDc3Attestations({
        page: currentPage,
        perPage: rowsPerPage,
        search: searchText || undefined,
        order,
      })

      setAttestations(response.data)
      setTotal(response.total)
    } finally {
      setLoading(false)
    }
  }, [currentPage, rowsPerPage, searchText, sort])

  useEffect(() => {
    fetchAttestations()
  }, [fetchAttestations])

  useEffect(() => {
    onSecondaryViewChange?.(formView !== null || associateView !== null)
  }, [formView, associateView, onSecondaryViewChange])

  const openCreateForm = useCallback(() => {
    setFormView({ mode: 'create' })
  }, [])

  const openEditForm = useCallback((attestation: IDc3Attestation) => {
    setFormView({ mode: 'edit', id: attestation.id })
  }, [])

  const closeForm = useCallback(() => {
    setFormView(null)
  }, [])

  const closeAssociateView = useCallback(() => {
    setAssociateView(null)
  }, [])

  const handleAttestationSaved = useCallback(
    (attestation: IDc3AttestationDetail) => {
      setFormView(null)
      setAssociateView({ attestationId: attestation.id })
    },
    []
  )

  const handleBackToForm = useCallback(() => {
    if (!associateView) return
    setAssociateView(null)
    setFormView({ mode: 'edit', id: associateView.attestationId })
  }, [associateView])

  const handleSkipAssociation = useCallback(() => {
    closeAssociateView()
    notification.success(
      t(
        'administration.certificate.legalAttestationAssociateCourses.notifications.skipSuccess'
      )
    )
    fetchAttestations()
  }, [closeAssociateView, notification, t, fetchAttestations])

  const handleSaveAssociation = useCallback(
    async (courseIds: number[]) => {
      if (!associateView) return

      try {
        await dc3AttestationsService.syncAssociatedCourses(
          associateView.attestationId,
          courseIds
        )
        closeAssociateView()
        notification.success(
          t(
            'administration.certificate.legalAttestationAssociateCourses.notifications.associationSuccess',
            { count: courseIds.length }
          )
        )
        await fetchAttestations()
      } catch {
        notification.error(
          t(
            'administration.certificate.legalAttestationAssociateCourses.notifications.associationError'
          )
        )
      }
    },
    [associateView, closeAssociateView, notification, t, fetchAttestations]
  )

  const onRowsPerPageChange = (value: number) => {
    setCurrentPage(1)
    setRowsPerPage(value)
  }

  const onSearch = (value: string) => {
    setCurrentPage(1)
    setSearchText(value)
  }

  const onSortChange = (value: SortValue) => {
    setCurrentPage(1)
    setSort(value.order ? value : null)
  }

  const openDeleteModal = useCallback((attestation: IDc3Attestation) => {
    setSelectedAttestation(attestation)
    setDeleteModalOpen(true)
  }, [])

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false)
    setSelectedAttestation(null)
  }, [])

  const openAssociatedCoursesModal = useCallback((attestationId: number) => {
    setAssociatedCoursesAttestationId(attestationId)
  }, [])

  const closeAssociatedCoursesModal = useCallback(() => {
    setAssociatedCoursesAttestationId(null)
  }, [])

  const handleDeleteAttestation = async () => {
    if (!selectedAttestation) return

    setDeleteProcessing(true)
    try {
      await dc3AttestationsService.destroyDc3Attestation(selectedAttestation.id)
      closeDeleteModal()
      notification.success(t(`${prefix}.deleteSuccess`))
      await fetchAttestations()
    } finally {
      setDeleteProcessing(false)
    }
  }

  const handleDuplicateAttestation = useCallback(
    async (attestation: IDc3Attestation) => {
      if (duplicatingId !== null) return

      setDuplicatingId(attestation.id)
      try {
        await dc3AttestationsService.duplicateDc3Attestation(attestation.id)
        notification.success(t(`${prefix}.duplicateSuccess`))
        await fetchAttestations()
      } catch {
        notification.error(t(`${prefix}.duplicateError`))
      } finally {
        setDuplicatingId(null)
      }
    },
    [duplicatingId, fetchAttestations, notification, prefix, t]
  )

  const locale = useMemo(
    () => ({
      header: {
        addButton: t(`${prefix}.createButton`),
        rowsCount: t(`${prefix}.table.rowsCount`),
        rowsCount_plural: t(`${prefix}.table.rowsCount_plural`),
      },
    }),
    [t, prefix]
  )

  const emptyState = useMemo(
    () => (
      <EmptyState
        title={t(`${prefix}.emptyState.title`)}
        description={t(`${prefix}.emptyState.description`)}
        icon={faFileCertificate}
        action={openCreateForm}
        actionIcon={faPlus}
        actionTitle={t(`${prefix}.emptyState.actionTitle`)}
        actionBackground={false}
      />
    ),
    [t, prefix, openCreateForm]
  )

  const columns = useMemo(
    () => [
      {
        title: t(`${prefix}.table.headers.name`),
        dataIndex: 'name',
        key: 'name',
        hideable: false,
        sorteable: true,
        render: (name: string) => <div>{name}</div>,
      },
      {
        title: t(`${prefix}.table.headers.createdAt`),
        dataIndex: 'createdAt',
        key: 'createdAt',
        sorteable: true,
        width: 170,
        render: (createdAt: string) => (
          <div>{format(new Date(createdAt), 'dd-MM-yyyy HH:mm')}</div>
        ),
      },
      {
        title: t(`${prefix}.table.headers.courses`),
        dataIndex: 'associatedCoursesCount',
        key: 'associatedCoursesCount',
        sorteable: true,
        width: 180,
        render: (count: number, data: IDc3Attestation) => (
          <AssociatedCoursesCell
            attestationId={data.id}
            count={count}
            onOpen={openAssociatedCoursesModal}
          />
        ),
      },
      {
        width: 66,
        hideable: false,
        align: 'center' as const,
        fixed: 'right' as const,
        render: (_: unknown, data: IDc3Attestation) => (
          <Dropdown
            placement="bottomRight"
            overlay={
              <Menu>
                <Menu.Item onClick={() => openEditForm(data)}>
                  {t(`${prefix}.table.actions.edit`)}
                </Menu.Item>
                <Menu.Item
                  disabled={duplicatingId === data.id}
                  onClick={() => handleDuplicateAttestation(data)}
                >
                  {t(`${prefix}.table.actions.duplicate`)}
                </Menu.Item>
                <Menu.Item onClick={() => openDeleteModal(data)}>
                  {t(`${prefix}.table.actions.delete`)}
                </Menu.Item>
              </Menu>
            }
          >
            <Options>
              <Button type="link">
                <FontAwesomeIcon icon={faEllipsisV} />
              </Button>
            </Options>
          </Dropdown>
        ),
      },
    ],
    [
      t,
      prefix,
      openAssociatedCoursesModal,
      openDeleteModal,
      openEditForm,
      duplicatingId,
      handleDuplicateAttestation,
    ]
  )

  if (associateView) {
    return (
      <AssociateCoursesView
        attestationId={associateView.attestationId}
        onBackToForm={handleBackToForm}
        onSkip={handleSkipAssociation}
        onSaveAssociation={handleSaveAssociation}
      />
    )
  }

  if (formView) {
    return (
      <Dc3AttestationForm
        mode={formView.mode}
        attestationId={formView.mode === 'edit' ? formView.id : undefined}
        onCancel={closeForm}
        onSaved={handleAttestationSaved}
      />
    )
  }

  return (
    <RankmiTableWrapper>
      <TableHeaderActions>
        {dc4Enabled ? (
          <Button
            type="link"
            data-testid="dc4-report-open-modal"
            onClick={() => setDc4ModalOpen(true)}
          >
            {t(`${prefix}.dc4ReportButton`)}
          </Button>
        ) : null}
        <Button
          type="primary"
          data-testid="dc3-attestation-create"
          onClick={openCreateForm}
        >
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: 8 }} />
          {t(`${prefix}.createButton`)}
        </Button>
      </TableHeaderActions>
      <RankmiTable
        columnHeaders={columns}
        data={attestations}
        countIcon={faFileCertificate}
        search={searchText}
        onSearch={onSearch}
        total={total}
        locale={locale}
        changeColumnsVisibility
        loading={loading}
        currentPage={currentPage}
        onPaginationChange={setCurrentPage}
        initialRowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        onSortChange={onSortChange}
        delaySearch
        responsive
        customEmpty={emptyState}
        showFooter={total > 0}
      />
      {deleteModalOpen && (
        <DeleteAttestationModal
          isOpen={deleteModalOpen}
          processing={deleteProcessing}
          onClose={closeDeleteModal}
          onConfirm={handleDeleteAttestation}
        />
      )}
      {associatedCoursesAttestationId !== null && (
        <AssociatedCoursesModal
          attestationId={associatedCoursesAttestationId}
          onClose={closeAssociatedCoursesModal}
        />
      )}
      {dc4Enabled ? (
        <Dc4ReportGenerateModal
          open={dc4ModalOpen}
          onClose={() => setDc4ModalOpen(false)}
        />
      ) : null}
    </RankmiTableWrapper>
  )
}

export default LegalAttestations
